import React from "react";
import { Page, Text, View, Document, StyleSheet, Image, Link } from "@react-pdf/renderer";

const styles = StyleSheet.create({
  page: { padding: 40, fontSize: 10, fontFamily: "Helvetica", color: "#000" },
  header: { marginBottom: 15, position: "relative" },
  name: { fontSize: 20, fontWeight: "bold", marginBottom: 5 },
  contact: { fontSize: 9, marginBottom: 5, color: "#333" },
  links: { fontSize: 9, flexDirection: "row", flexWrap: "wrap", gap: 12 },
  link: { color: "#1a0dab", textDecoration: "underline" },
  photo: { 
    width: 80, 
    height: 80, 
    borderRadius: 40, 
    position: "absolute", 
    right: 0, 
    top: -10,
    border: "2pt solid #000" 
  },
  section: { marginBottom: 12 },
  sectionTitle: { 
    fontSize: 11, 
    fontWeight: "bold", 
    textTransform: "uppercase", 
    borderBottom: 1, 
    paddingBottom: 2, 
    marginBottom: 6 
  },
  profileSection: { marginBottom: 15 },
  profileText: { 
    fontSize: 9.5, 
    lineHeight: 1.4, 
    textAlign: "justify",
    marginTop: 4
  },
  item: { marginBottom: 8 },
  itemHeader: { flexDirection: "row", justifyContent: "space-between", marginBottom: 2 },
  role: { fontSize: 10, fontWeight: "bold" },
  company: { fontSize: 9, fontStyle: "italic" },
  date: { fontSize: 9, color: "#555" },
  bullet: { fontSize: 9, marginLeft: 10, marginBottom: 1 },
  twoCol: { flexDirection: "row", marginTop: 10, gap: 20 },
  leftCol: { flex: 2 },
  rightCol: { flex: 1 },
  langItem: { flexDirection: "row", justifyContent: "space-between", marginBottom: 3 },
  certItem: { marginBottom: 4 }
});

const PDFDocument = ({ formData }) => {
  const { 
    name, address, email, phone, linkedin, github, portfolio, photo, summary, 
    education, experience, projects, skills, languages, certifications 
  } = formData;

  const hasLink = (url) => url && url.trim() !== '';

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* HEADER */}
        <View style={styles.header}>
          {photo && <Image style={styles.photo} src={photo} />}
          <Text style={styles.name}>{name || 'Votre Nom'}</Text>
          {(address || email || phone) && (
            <Text style={styles.contact}>
              {address && `${address}`}
              {email && ` | ${email}`}
              {phone && ` | ${phone}`}
            </Text>
          )}
          {(linkedin || github || portfolio) && (
            <View style={styles.links}>
              {hasLink(linkedin) && <Link src={`https://${linkedin}`} style={styles.link}>LinkedIn</Link>}
              {hasLink(github) && <Link src={`https://${github}`} style={styles.link}>GitHub</Link>}
              {hasLink(portfolio) && <Link src={`https://${portfolio}`} style={styles.link}>Portfolio</Link>}
            </View>
          )}
        </View>

        {/* PROFIL - Section ajoutée comme dans le PDF donné */}
        {summary && (
          <View style={styles.profileSection}>
            <Text style={styles.sectionTitle}>PROFIL</Text>
            <Text style={styles.profileText}>{summary}</Text>
          </View>
        )}

        {/* FORMATION */}
        {education.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Formation</Text>
            {education.map((edu, i) => (
              <View key={i} style={styles.item}>
                <View style={styles.itemHeader}>
                  <Text style={styles.role}>{edu.school}</Text>
                  <Text style={styles.date}>{edu.start} - {edu.end}</Text>
                </View>
                <Text style={styles.company}>{edu.degree}</Text>
              </View>
            ))}
          </View>
        )}

        {/* EXPÉRIENCE */}
        {experience.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Expérience Professionnelle</Text>
            {experience.map((exp, i) => (
              <View key={i} style={styles.item}>
                <View style={styles.itemHeader}>
                  <Text style={styles.role}>{exp.role}</Text>
                  <Text style={styles.date}>{exp.start} - {exp.end}</Text>
                </View>
                <Text style={styles.company}>{exp.company} - {exp.location}</Text>
                {exp.description.split('\n').map((line, j) => line.trim() && (
                  <Text key={j} style={styles.bullet}>• {line.trim()}</Text>
                ))}
              </View>
            ))}
          </View>
        )}

        {/* DEUX COLONNES */}
        <View style={styles.twoCol}>
          <View style={styles.leftCol}>
            {/* PROJETS */}
            {projects.length > 0 && (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>Projets Académiques</Text>
                {projects.map((proj, i) => (
                  <View key={i} style={styles.item}>
                    <View style={styles.itemHeader}>
                      <Text style={styles.role}>{proj.name}</Text>
                      <Text style={styles.date}>{proj.date}</Text>
                    </View>
                    <Text style={styles.company}>Technologies: {proj.technologies}</Text>
                    {proj.description.split('\n').map((line, j) => line.trim() && (
                      <Text key={j} style={styles.bullet}>• {line.trim()}</Text>
                    ))}
                  </View>
                ))}
              </View>
            )}

            {/* COMPÉTENCES */}
            {Object.values(skills).flat().filter(s => s.trim()).length > 0 && (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>Compétences Techniques</Text>
                {Object.entries(skills).map(([cat, list]) => list.filter(s => s.trim()).length > 0 && (
                  <View key={cat} style={styles.item}>
                    <Text style={styles.role}>
                      {cat === "programming" ? "• Langages de programmation" :
                       cat === "frameworks" ? "• Frameworks & Bibliothèques" :
                       cat === "databases" ? "• Bases de données" :
                       cat === "tools" ? "• Gestion de projet" :
                       cat === "dataAnalysis" ? "• Analyse de données" : cat}:
                    </Text>
                    <Text style={styles.bullet}>{list.filter(s => s.trim()).join(', ')}</Text>
                  </View>
                ))}
              </View>
            )}
          </View>

          <View style={styles.rightCol}>
            {/* LANGUES */}
            {languages.length > 0 && (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>Langues</Text>
                {languages.map((lang, i) => (
                  <View key={i} style={styles.langItem}>
                    <Text>• {lang.language}</Text>
                    <Text>{lang.level}</Text>
                  </View>
                ))}
              </View>
            )}

            {/* CERTIFICATIONS */}
            {certifications.length > 0 && (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>Certifications</Text>
                {certifications.map((cert, i) => (
                  <View key={i} style={styles.certItem}>
                    <Text style={styles.role}>• {cert.name}</Text>
                    <Text style={styles.company}>{cert.issuer} - {cert.date}</Text>
                  </View>
                ))}
              </View>
            )}
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default PDFDocument;